import React, { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import api from '../../../utils/api';
import { useAuth } from '../../../context/AuthContext';
import { canAssignCrmTickets, canCreateCrmTickets, canViewCrmTicketReports } from '../../../utils/crmPermissions';
import { Button } from '../../ui/button';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../ui/select';
import { Plus, BarChart3, Columns3, List, FileText } from 'lucide-react';
import CrmTicketDashboard from './CrmTicketDashboard';
import CrmTicketKanban from './CrmTicketKanban';
import CrmTicketList from './CrmTicketList';
import CrmTicketCreateDialog from './CrmTicketCreateDialog';
import { isMobileContainer } from '../../../utils/device';

const TABS = [
  { id: 'dashboard', label: 'Dashboard', icon: BarChart3 },
  { id: 'kanban', label: 'Board', icon: Columns3 },
  { id: 'list', label: 'List', icon: List },
];

function employeeLabel(emp) {
  const name = `${emp.first_name || ''} ${emp.last_name || ''}`.trim();
  return name ? `${name} (${emp.employee_code})` : emp.employee_code;
}

export default function CrmTickets() {
  const { user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const defaultTab = isMobileContainer() ? 'list' : 'dashboard';
  const [tab, setTab] = useState(defaultTab);
  const [createOpen, setCreateOpen] = useState(false);
  const [statuses, setStatuses] = useState([]);
  const [listRefreshKey, setListRefreshKey] = useState(0);
  const [employees, setEmployees] = useState([]);
  const [relatedEmployeeId, setRelatedEmployeeId] = useState('all');
  const [listFilters, setListFilters] = useState({ status: 'all', overdue: false });
  const isMobile = isMobileContainer();
  const canCreate = canCreateCrmTickets(user);
  const canPickEmployee = canAssignCrmTickets(user);

  useEffect(() => {
    api.get('/crm/tickets/meta').then((r) => setStatuses(r.data?.data?.statuses || [])).catch(() => {});
  }, []);

  useEffect(() => {
    if (!canPickEmployee) return;
    api.get('/crm/employees')
      .then((r) => setEmployees((r.data?.data?.items || []).filter((e) => e.status === 'active')))
      .catch(() => setEmployees([]));
  }, [canPickEmployee]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const t = params.get('tab');
    if (t && TABS.some((x) => x.id === t)) {
      setTab(t);
      if (t === 'list') {
        setListFilters({
          status: params.get('status') || 'all',
          overdue: params.get('overdue') === '1',
        });
      } else {
        setListFilters({ status: 'all', overdue: false });
      }
      return;
    }
    if (isMobileContainer() && !t) {
      navigate('/crm/tickets?tab=list', { replace: true });
      setTab('list');
    }
  }, [location.search, navigate]);

  const setTabAndUrl = (id) => {
    setTab(id);
    setListFilters({ status: 'all', overdue: false });
    navigate(`/crm/tickets?tab=${id}`, { replace: true });
  };

  const onChipClick = (filter = {}) => {
    const params = new URLSearchParams({ tab: 'list' });
    if (filter.status) params.set('status', filter.status);
    if (filter.overdue) params.set('overdue', '1');
    setListFilters({
      status: filter.status || 'all',
      overdue: !!filter.overdue,
    });
    setTab('list');
    navigate(`/crm/tickets?${params.toString()}`, { replace: true });
  };

  const employeeFilterId = relatedEmployeeId !== 'all' ? relatedEmployeeId : '';

  return (
    <div className="space-y-6" data-testid="crm-tickets-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold">Tickets</h1>
          <p className="text-muted-foreground text-sm">Track work, support, and operational tasks</p>
        </div>
        <div className="flex flex-col sm:flex-row gap-2 sm:items-center">
          {canPickEmployee && (
            <Select value={relatedEmployeeId} onValueChange={setRelatedEmployeeId}>
              <SelectTrigger className="w-full sm:w-56" data-testid="ticket-employee-picker">
                <SelectValue placeholder="All employees" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All employees</SelectItem>
                {employees.map((e) => (
                  <SelectItem key={e.id} value={String(e.id)}>{employeeLabel(e)}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
          {canViewCrmTicketReports(user) && (
            <Button variant="outline" asChild>
              <Link to="/crm/tickets/reports"><FileText className="h-4 w-4 mr-2" />Reports</Link>
            </Button>
          )}
          {canCreate && !isMobile && (
            <Button onClick={() => setCreateOpen(true)}><Plus className="h-4 w-4 mr-2" />Create Ticket</Button>
          )}
        </div>
      </div>

      <div className="flex gap-1 border-b">
        {TABS.map(({ id, label, icon: Icon }) => (
          <button
            key={id}
            type="button"
            onClick={() => setTabAndUrl(id)}
            className={`flex items-center gap-2 px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${
              tab === id ? 'border-primary text-primary' : 'border-transparent text-muted-foreground hover:text-foreground'
            }`}
          >
            <Icon className="h-4 w-4" />{label}
          </button>
        ))}
      </div>

      {tab === 'dashboard' && (
        <CrmTicketDashboard relatedEmployeeId={employeeFilterId} onChipClick={onChipClick} />
      )}
      {tab === 'kanban' && <CrmTicketKanban relatedEmployeeId={employeeFilterId} />}
      {tab === 'list' && (
        <CrmTicketList
          statuses={statuses}
          refreshKey={listRefreshKey}
          relatedEmployeeId={employeeFilterId}
          listFilters={listFilters}
        />
      )}

      {isMobile && canCreate && (
        <button
          type="button"
          onClick={() => setCreateOpen(true)}
          aria-label="Create Ticket"
          data-testid="floating-create-ticket-button"
          className="fixed z-50 left-4 bottom-[calc(var(--mobile-nav-offset,0px)+1rem)] flex items-center h-12 px-4 rounded-full gap-2 bg-primary text-primary-foreground shadow-lg active:scale-95"
        >
          <Plus className="h-5 w-5 shrink-0" />
          <span className="text-sm font-semibold whitespace-nowrap">New Ticket</span>
        </button>
      )}

      <CrmTicketCreateDialog
        open={createOpen}
        onOpenChange={setCreateOpen}
        onCreated={() => {
          setTabAndUrl('list');
          setListRefreshKey((k) => k + 1);
        }}
      />
    </div>
  );
}
